/**
 * ImageLibrary — hosted images for signatures (headshots, logos, banners,
 * social icons). Uploads are downscaled/compressed client-side, stored in
 * Firebase Storage under email-images/{uid}/signature/ (public-read per
 * storage.rules — Gmail signature images must be publicly reachable), and
 * tracked in an inventory the FutureBot tools can reference by name.
 *
 * Removing an image only removes it from this library — the hosted file is
 * kept so signatures already installed (and emails already sent) keep
 * rendering it.
 */

import React, { useRef, useState } from 'react';
import {
  AspectRatio,
  Box,
  Button,
  HStack,
  Image,
  SimpleGrid,
  Text,
  Tooltip,
  useColorModeValue,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { FiCopy, FiPlus, FiTrash2, FiUpload } from 'react-icons/fi';
import { firebase } from '../../../utils/init-firebase';
import { useAuth } from '../../../contexts/AuthContext';

const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/gif'];
const MAX_UPLOAD_BYTES = 5 * 1024 * 1024;
const MAX_DIMENSION = 600; // 2x a 300px display size — plenty for signatures

function loadImageFromFile(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new window.Image();
    img.onload = () => resolve({ img, revoke: () => URL.revokeObjectURL(url) });
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Could not read that image file.')); };
    img.src = url;
  });
}

/** Downscale + compress. GIFs pass through untouched (canvas kills animation). */
async function processImageFile(file) {
  if (file.type === 'image/gif') {
    const { img, revoke } = await loadImageFromFile(file);
    const dims = { width: img.naturalWidth, height: img.naturalHeight };
    revoke();
    return { blob: file, ext: 'gif', ...dims };
  }
  const { img, revoke } = await loadImageFromFile(file);
  const scale = Math.min(1, MAX_DIMENSION / Math.max(img.naturalWidth, img.naturalHeight));
  const width = Math.max(1, Math.round(img.naturalWidth * scale));
  const height = Math.max(1, Math.round(img.naturalHeight * scale));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx2d = canvas.getContext('2d');
  ctx2d.drawImage(img, 0, 0, width, height);
  revoke();
  const isPng = file.type === 'image/png'; // keep PNG for transparency (logos)
  const blob = await new Promise((resolve) => canvas.toBlob(resolve, isPng ? 'image/png' : 'image/jpeg', 0.85));
  if (!blob) throw new Error('Image processing failed.');
  return { blob, ext: isPng ? 'png' : 'jpg', width, height };
}

export default function ImageLibrary({ images, onImagesChange, onUseImage }) {
  const { currentUser } = useAuth();
  const toast = useToast();
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const cardBG = useColorModeValue('gray.50', 'gray.700');
  const mutedColor = useColorModeValue('gray.600', 'gray.400');

  const handleFiles = async (e) => {
    const file = e.target.files?.[0];
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (!file || !currentUser?.uid) return;
    if (!ALLOWED_TYPES.includes(file.type)) {
      toast({ title: 'Use PNG, JPEG, or GIF', description: 'SVG and WebP break in email clients. Transparent PNG is best for logos.', status: 'warning', duration: 5000 });
      return;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      toast({ title: 'File too large', description: 'Images must be under 5 MB.', status: 'warning', duration: 4000 });
      return;
    }
    setUploading(true);
    try {
      const processed = await processImageFile(file);
      const safeName = file.name.replace(/\.[^.]+$/, '').replace(/[^\w-]+/g, '-').slice(0, 40) || 'image';
      const path = `email-images/${currentUser.uid}/signature/${Date.now()}_${safeName}.${processed.ext}`;
      const storageRef = firebase.storage().ref(path);
      await storageRef.put(processed.blob, { contentType: processed.blob.type || file.type });
      const url = await storageRef.getDownloadURL();
      const record = {
        id: `img_${Date.now()}`,
        name: `${safeName}.${processed.ext}`,
        url,
        width: processed.width,
        height: processed.height,
        uploadedAt: Date.now(),
      };
      onImagesChange([record, ...(images || [])]);
      toast({ title: 'Image uploaded', description: 'It\'s hosted and ready to use in your signature.', status: 'success', duration: 3000 });
    } catch (err) {
      console.error('[SignatureStudio] upload failed:', err);
      toast({ title: 'Upload failed', description: err.message, status: 'error', duration: 5000 });
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (img) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Remove "${img.name}" from your library?\n\nThe hosted file is kept so signatures and emails already using it keep working.`)) return;
    onImagesChange((images || []).filter((i) => i.id !== img.id));
  };

  const copyUrl = async (img) => {
    try {
      await navigator.clipboard.writeText(img.url);
      toast({ title: 'Image URL copied', status: 'success', duration: 2000 });
    } catch (err) {
      toast({ title: 'Copy failed', status: 'error', duration: 3000 });
    }
  };

  return (
    <VStack align="stretch" spacing={3}>
      <input
        type="file"
        ref={fileInputRef}
        accept={ALLOWED_TYPES.join(',')}
        style={{ display: 'none' }}
        onChange={handleFiles}
      />
      <Button size="sm" leftIcon={<FiUpload />} colorScheme="purple" variant="outline"
        onClick={() => fileInputRef.current?.click()} isLoading={uploading} loadingText="Uploading…">
        Upload image
      </Button>
      <Text fontSize="10px" color={mutedColor}>
        Headshots, logos, banners, icons. PNG with a transparent background is best for logos (JPEG glows white in dark mode).
        Uploads are auto-resized and hosted publicly — required for Gmail.
      </Text>

      {(!images || images.length === 0) && (
        <Box borderWidth="1px" borderStyle="dashed" borderColor={borderColor} borderRadius="md" p={4} textAlign="center">
          <Text fontSize="xs" color={mutedColor}>No images yet. Upload your headshot or the company logo to get started.</Text>
        </Box>
      )}

      <SimpleGrid columns={2} spacing={2}>
        {(images || []).map((img) => (
          <Box key={img.id} borderWidth="1px" borderColor={borderColor} borderRadius="md" overflow="hidden" bg={cardBG}>
            <AspectRatio ratio={4 / 3}>
              <Image src={img.url} alt={img.name} objectFit="contain" bg="white" />
            </AspectRatio>
            <Box p={1.5}>
              <Text fontSize="10px" fontWeight="semibold" isTruncated title={img.name}>{img.name}</Text>
              <Text fontSize="9px" color={mutedColor}>{img.width}×{img.height}px</Text>
              <HStack spacing={0.5} mt={1}>
                <Tooltip label="Add to signature"><Button size="xs" variant="ghost" onClick={() => onUseImage(img)} px={1.5}><FiPlus /></Button></Tooltip>
                <Tooltip label="Copy hosted URL"><Button size="xs" variant="ghost" onClick={() => copyUrl(img)} px={1.5}><FiCopy /></Button></Tooltip>
                <Tooltip label="Remove from library"><Button size="xs" variant="ghost" colorScheme="red" onClick={() => removeImage(img)} px={1.5}><FiTrash2 /></Button></Tooltip>
              </HStack>
            </Box>
          </Box>
        ))}
      </SimpleGrid>
    </VStack>
  );
}
